/* eslint-disable no-console */
/**
 * Error logging
 *
 * Can be used to log errors to console or and eternal
 * error logging system, like Sentry for example.
 *
 */

import * as Sentry from '@sentry/browser';
import config from '../config';
import { responseApiErrorInfo } from './errors';
import { shouldFilterError, sanitizeForSentry } from '../shared/sentryFilters';

const PRIORITY_ERROR_CODES = new Set([
  'special-offer-order-failed',
  'initiate-order-failed',
  'confirm-payment-failed',
  'initial-message-send-failed',
  'speculate-transaction-failed',
  'stripe-checkout-form-submit-failed',
  'stripe-confirm-failed',
  'stripe-checkout-page-form-submit-failed',
  'confirm-special-offer-failed',
  'accept-sale-failed',
  'accept-sale-failed-capture',
  'cancel-sale-failed',
  'reject-sale-failed',
  'request-changes-customer-failed',
  'accept-changes-failed',
  'decline-changes-failed',
  'user-pending-approval',
  'user-email-unverified',
  'sharetribe-initiate-failed',
  'inquiry-not-created',
  'landowner-insurance-create-subscription-failed',
  'landowner-insurance-payment-failed',
  'field-wysiwyg-error-boundary',
]);
const DEFAULT_ERROR_SAMPLE_RATE = 0.25;

/**
 * Set up error handling. If a Sentry DSN is
 * provided a Sentry client will be installed.
 */
export const setup = () => {
  if (config.sentryDsn) {
    // Configures the Sentry client. Adds a handler for
    // any uncaught exception.
    Sentry.init({
      dsn: config.sentryDsn,
      environment: config.env,
      // Set release version from environment variable (injected during build)
      release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
      ignoreErrors: [
        "SyntaxError: Failed to execute 'evaluate' on 'Document': The string '//form[@method='post' or @method='POST']",
        'SecurityError: The operation is insecure.',
        'Failed to initialize WebGL.',
        "Unexpected token '<'",
        'JSON Parse error: Unexpected EOF',
        'Illegal invocation',
        'Invalid LngLat object: (NaN, NaN)',
        'Network Error',
        'InitializationError',
        /Non-Error exception captured/i,
        "Cannot declare a let variable twice: 't'.",
        "undefined is not an object (evaluating 'window.webkit.messageHandlers')",
        /google_tag_manager/,
        /ChunkLoadError/i,
        /Loading chunk \d+ failed/i,
        /failed after \d+ retries/i,
        /invalid origin/i,
        /Non-Error promise rejection/i,
        /XMLHttpRequestProgressEvent/i,
        /Failed to fetch/i,
        /fetch.*failed/i,
        /cyclic object value/i,
        /Converting circular structure to JSON/i,
        /GeolocationPositionError/i,
        'User denied Geolocation',
        /EmptyRanges/i,
        "Can't find variable: EmptyRanges",
        /window\[OT\]/i,
        /recaptcha/i,
        /NetworkError when attempting to fetch resource/i,
        /window\.clarity.*is not a function/i,
        /window\.clarity.*is undefined/i,
        /ResizeObserver loop completed with undelivered notifications/i,
        /Failed to execute 'removeChild' on 'Node'/i,
        /The node to be removed is not a child of this node/i,
        'Request aborted',
        /feature named .* was not found/i,
        /Request failed with status code 404\b/i,
        /\bstatus code 404\b/i,
      ],
      denyUrls: [
        /googletagmanager\.com/,
        /api\.mapbox\.com/,
        /clarity\.ms/,
        /static\.klaviyo\.com/,
      ],
      tracesSampleRate: 0.1, // only send 10% of transactions
      // Use full SDK capture, then sample selectively in beforeSend so
      // priority error codes can always bypass sampling.
      sampleRate: 1,
      beforeSend(event, hint) {
        try {
          if (shouldFilterError(event, hint)) {
            return null;
          }

          const errorCode = event.tags?.code;
          if (!PRIORITY_ERROR_CODES.has(errorCode) && Math.random() >= DEFAULT_ERROR_SAMPLE_RATE) {
            return null;
          }
        } catch (filterErr) {
          // eslint-disable-next-line no-console
          console.error('[sentry:beforeSend] filter crashed — forwarding original event', {
            filterError: filterErr?.message,
            originalError: hint?.originalException?.message,
            originalName: hint?.originalException?.name,
            originalCode: hint?.originalException?.code,
            originalCodeType: typeof hint?.originalException?.code,
            originalStatus: hint?.originalException?.status,
          });
        }
        return event;
      },
    });

    // Add global promise rejection handler to catch unhandled promise rejections
    window.addEventListener('unhandledrejection', event => {
      const { reason } = event;
      // eslint-disable-next-line no-console
      console.warn('[sentry] unhandled promise rejection', {
        type: typeof reason,
        name: reason?.name,
        message: reason?.message,
        status: reason?.status,
        code: reason?.code,
        codeType: typeof reason?.code,
        constructor: reason?.constructor?.name,
      });
      Sentry.captureException(reason, {
        tags: {
          type: 'unhandled_promise_rejection',
        },
        extra: {
          promiseRejected: true,
          reasonType: typeof reason,
          reasonName: reason?.name,
          reasonCode: String(reason?.code ?? ''),
          reasonCodeType: typeof reason?.code,
          reasonStatus: reason?.status,
        },
      });
    });
  }
};

/**
 * Set user ID for the logger so that it
 * can be attached to Sentry issues.
 *
 * @param {String} userId ID of current user
 */
export const setUserId = userId => {
  Sentry.setUser({ id: userId });
};

/**
 * Set comprehensive user context for Sentry.
 * This includes user ID, email, username, and other relevant metadata.
 *
 * @param {Object} user - User object containing user details
 * @param {String} user.id - User ID (required)
 * @param {String} user.email - User email
 * @param {String} user.username - Username or display name
 * @param {Object} user.attributes - User attributes (profile data)
 */
export const setUserContext = user => {
  // Validate that user exists and has an id before sending to Sentry
  if (!user || !user.id) {
    return;
  }

  const userData = {
    id: user.id?.uuid || user.id,
  };

  // Add email if available
  if (user.attributes?.email) {
    userData.email = user.attributes.email;
  }

  // Set user in Sentry
  Sentry.setUser(userData);
};

/**
 * Clears the user ID and all user context from Sentry.
 */
export const clearUserId = () => {
  Sentry.setUser(null);
};

const printAPIErrorsAsConsoleTable = apiErrors => {
  if (apiErrors != null && apiErrors.length > 0 && typeof console.table === 'function') {
    // eslint-disable-next-line no-console
    console.log('Errors returned by Marketplace API call:');
    // eslint-disable-next-line no-console
    console.table(apiErrors.map(err => ({ status: err.status, code: err.code, ...err.meta })));
  }
};

/**
 * Logs an execption. If Sentry is configured
 * sends the error information there. Otherwise
 * prints the error to the console.
 *
 * @param {Error} e Error that occurred
 * @param {String} code Error code
 * @param {Object} data Additional data to be sent to Sentry
 */
export const error = (e, code, data) => {
  const apiErrors = responseApiErrorInfo(e);
  console.log('sentry is configured:', !!config.sentryDsn);

  if (config.sentryDsn) {
    // Sanitize data to prevent circular reference errors
    const sanitizedData = data ? sanitizeForSentry(data) : {};
    const extra = { ...sanitizedData, apiErrorData: apiErrors };

    Sentry.withScope(scope => {
      scope.setTag('code', code);
      Object.keys(extra).forEach(key => {
        scope.setExtra(key, extra[key]);
      });
      Sentry.captureException(e);
    });

    printAPIErrorsAsConsoleTable(apiErrors);
  } else {
    // eslint-disable-next-line no-console
    console.error(e);
    // eslint-disable-next-line no-console
    console.error('Error code:', code, 'data:', data);
    printAPIErrorsAsConsoleTable(apiErrors);
  }
};
